import { Component, Host, h, Element, Prop } from '@stencil/core';
import { addEventListener, closestElement, removeEventListener } from '../../utils/helpers';

@Component({
  tag: 'vds-pagination-pill',
  styleUrl: 'vds-pagination-pill.scss',
  shadow: true,
})
export class VdsPaginationPill {
  @Element() el: HTMLVdsPaginationProgressElement;

  /**
   * Max number of the pills. Automatically set when slides reference is found.
   */
  @Prop({ mutable: true, reflect: true }) max: number = 0;

  /**
   * Current pill index. Automatically set when slides reference is found.
   */
  @Prop({ mutable: true, reflect: true }) index: number = 1;

  /**
   * Connected Slides HTML Element
   */
  @Prop({ mutable: true }) slides?: HTMLVdsSlidesElement;

  /**
   * CSS Selector Reference to Slides HTML Element
   */
  @Prop() slidesRef?: string;

  render() {
    const { max } = this;

    return (
      <Host>
        { max > 0 && (
          <ul class="w-fit flex flex-wrap ml-[-12px] mt-[20px]">
            { this.renderPills() }
          </ul>
        )}
      </Host>
    );
  }

  async connectedCallback(): Promise<void> {
    const slides = this.slides ? this.slides : (this.slides = this.getSlidesByReference(this.slidesRef));

    if (slides) {
      this.max = await slides.length() - 1;
      this.index = (await slides.getActiveIndex());
      addEventListener(slides, 'vdsSlideDidChange', () => this.onSlideDidChange(slides));
    }
  }

  disconnectedCallback(): void {
    const slides = this.slides;
    removeEventListener(slides, 'vdsSlideDidChange', () => this.onSlideDidChange(slides));
  }

  async componentDidRender(): Promise<void> {
    this.el.shadowRoot
      .querySelectorAll(".pill")
      .forEach((item, i) => {
        if (i == 0) {
          item.classList.add("active");
        }
    });
  }

  private renderPills(): Array<VdsPaginationPill> {
    let { index, max } = this;
    const pills: Array<VdsPaginationPill> = new Array();
    for( index; index <= max; index++ ) {
      const i = index;
      pills[i] =
        <li class="pl-[10px]">
          <a id={i.toString()} class="pill block w-[20px] md:w-[26px] h-[5px] rounded-[10px] bg-[length:200%_100%] cursor-pointer" onClick={() => this.slide(i)}></a>
        </li>
      ;
    }
    return pills;
  }

  private slide(id: number) {
    const item = this.el.shadowRoot.getElementById(id.toString());
    const items = this.el.shadowRoot.querySelectorAll(".pill");
    const slides = this.slides;
    for( var i = 0; i < items.length; i++ ) {
      items[i].classList.remove("active");
    }
    if (item) {
      item.classList.add("active");
    }
    slides.slideTo(id);
  }

  private getSlidesByReference(ref: string): HTMLVdsSlidesElement {
    if (ref) {
      return document.querySelector<HTMLVdsSlidesElement>(ref);
    } else {
      return (closestElement('vds-slides', this.el) as HTMLVdsSlidesElement) ?? document.querySelector('vds-slides');
    }
  }

  private async onSlideDidChange(slides: HTMLVdsSlidesElement): Promise<void> {
    const activeSlide = await slides.getActiveIndex();
    this.slide(activeSlide);
  }

}
